import { type DayOfEmergencyDto, type DayOfEmergencyParticipantDto } from "@crowdcoursing/api/openapi";

import { store } from "~services/providers/ReduxProvider";

/**
 * Loads today's UTC calendar day for the bubble. Returns null when the API responds 404 (no row yet).
 *
 * Uses raw `fetch` so HTTP status is preserved. The generated OpenAPI fetcher wraps non-OK responses in a way that
 * drops `status`, which breaks callers that treat 404 as an empty state (e.g. emergency command center).
 */
export async function fetchCurrentDayOfEmergencyForBubble(
  bubbleId: string,
  signal?: AbortSignal,
): Promise<DayOfEmergencyDto | null> {
  const baseUrl = import.meta.env.REACT_APP_BACKEND_URL as string | undefined;
  if (!baseUrl || !bubbleId) {
    throw new Error("Missing backend URL or bubble id.");
  }

  const url = `${String(baseUrl).replace(/\/$/, "")}/api/v1/Bubbles/${bubbleId}/day-of-emergency/current`;
  const token = store.getState().auth.token;
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }

    const err = new Error(detail || `HTTP ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }

  return (await res.json()) as DayOfEmergencyDto;
}

/** Aligns with API `EmergencyParticipantStatusKeys` (kebab-case strings). Display order for filters, chips, and map pin sort. */
export const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "Not reported", value: "not-reported" },
  { label: "Needs assistance", value: "needs-assistance" },
  { label: "Sheltering in place", value: "sheltering-in-place" },
  { label: "Evacuating", value: "evacuating" },
  { label: "Safe", value: "safe" },
  { label: "Absent / offsite", value: "absent-offsite" },
];

export const KNOWN_STATUS_VALUES = new Set(STATUS_OPTIONS.map((o) => o.value));

/**
 * Normalizes API status keys for counts, filters, and chips. Legacy `hiding` is treated as `sheltering-in-place`.
 */
export function canonicalEmergencyStatusKey(statusKey: string | null | undefined): string {
  const k = (statusKey ?? "not-reported").trim();
  if (k === "hiding") {
    return "sheltering-in-place";
  }
  return k.length > 0 ? k : "not-reported";
}

/**
 * Solid fill for emergency map participant stack pins (school and district maps). Keep in sync with map UX.
 */
export function emergencyStatusMapPinColor(statusKey: string | null | undefined): string {
  switch (statusKey) {
    case "safe":
      return "#2e7d32";
    case "needs-assistance":
      return "#c62828";
    case "hiding":
    case "sheltering-in-place":
      return "#ef6c00";
    case "evacuating":
      return "#6a1b9a";
    case "absent-offsite":
      return "#1565c0";
    case "not-reported":
      return "#757575";
    default:
      return "#1976d2";
  }
}

/**
 * Severity for location polygon fill (lower = worse). Core order: needs-assistance → sheltering in place → evacuating
 * → safe. Other known statuses are included so mixed polygons still pick a single worst color.
 */
const POLYGON_FILL_SEVERITY: Record<string, number> = {
  "absent-offsite": 5,
  evacuating: 3,
  "needs-assistance": 0,
  "not-reported": 1,
  safe: 4,
  "sheltering-in-place": 2,
};

function polygonFillSeverity(canonicalKey: string): number {
  return POLYGON_FILL_SEVERITY[canonicalKey] ?? 50;
}

/** Canonical status key with the worst (minimum) severity among participants, or null if empty. */
export function worstPolygonFillStatusKey(participants: readonly { statusKey?: string | null }[]): string | null {
  if (participants.length === 0) {
    return null;
  }
  let worstKey = canonicalEmergencyStatusKey(participants[0].statusKey);
  let worstSev = polygonFillSeverity(worstKey);
  for (let i = 1; i < participants.length; i++) {
    const k = canonicalEmergencyStatusKey(participants[i].statusKey);
    const s = polygonFillSeverity(k);
    if (s < worstSev || (s === worstSev && k.localeCompare(worstKey) < 0)) {
      worstSev = s;
      worstKey = k;
    }
  }
  return worstKey;
}

/** Fill color for a location polygon from the worst participant status; uses `fallbackColor` when nobody is listed. */
export function emergencyPolygonFillColor(
  participants: readonly { statusKey?: string | null }[],
  fallbackColor: string,
): string {
  const w = worstPolygonFillStatusKey(participants);
  if (w === null) {
    return fallbackColor;
  }
  return emergencyStatusMapPinColor(w);
}

/**
 * Aggregated buckets for the Everyone tab summary only (reporting still uses distinct statuses).
 * Hold uses sheltering only; legacy API `hiding` is canonicalized into sheltering for counts/filters.
 */
export const EVERYONE_SUMMARY_GROUPS: { id: string; label: string; statusValues: string[] }[] = [
  { id: "not-accounted", label: "Not accounted for", statusValues: ["not-reported"] },
  { id: "hiding-sheltering", label: "Hold", statusValues: ["sheltering-in-place"] },
  { id: "needs-assistance", label: "Needs assistance", statusValues: ["needs-assistance"] },
  { id: "evacuating", label: "Evacuating", statusValues: ["evacuating"] },
  { id: "safe", label: "Safe", statusValues: ["safe"] },
  { id: "absent-offsite", label: "Absent / offsite", statusValues: ["absent-offsite"] },
];

/** Summary card id for participants whose status is not in KNOWN_STATUS_VALUES (matches grid key). */
export const OTHER_LEGACY_SUMMARY_ID = "other-statuses";

export type ParticipantRow = DayOfEmergencyParticipantDto;

export function formatLocalDateTime(iso: string | undefined | null): string {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

export function statusLabelForKey(statusKey: string | undefined | null): string {
  if (!statusKey) {
    return "—";
  }
  if (statusKey === "with-students") {
    return "With students (legacy)";
  }
  if (statusKey === "hiding") {
    return STATUS_OPTIONS.find((o) => o.value === "sheltering-in-place")?.label ?? "Sheltering in place";
  }
  const opt = STATUS_OPTIONS.find((o) => o.value === statusKey);
  return opt?.label ?? statusKey;
}

export function timelineKindLabel(kind: string): string {
  switch (kind) {
    case "declaration-started":
      return "Dispatch";
    case "declaration-ended":
      return "Ended";
    case "status-reported":
      return "Status";
    case "plan-access":
      return "Plan access";
    default:
      return kind;
  }
}

/** API `kind` values for timeline entries; labels match `timelineKindLabel` for these. */
export const TIMELINE_ENTRY_KINDS = [
  "declaration-started",
  "declaration-ended",
  "status-reported",
  "plan-access",
] as const;

/**
 * When `selectedKinds` is empty, no kind filter (all entries, including unknown `kind` values).
 * Otherwise keep entries whose `kind` is in the selection (OR). Unknown kinds are excluded when any filter is active.
 */
export function timelineEntryMatchesKindFilter(kind: string | undefined | null, selectedKinds: string[]): boolean {
  if (selectedKinds.length === 0) {
    return true;
  }
  const k = kind ?? "";
  return selectedKinds.includes(k);
}

/** API returns 404 JSON (ProblemDetails) when there is no UTC-day record yet. */
export function isNotFoundFromApi(err: unknown): boolean {
  if (!err || typeof err !== "object") {
    return false;
  }
  const s = (err as { status?: unknown }).status;
  return s === 404 || s === "404";
}

export function participantDisplayName(p: ParticipantRow): string {
  const last = p.lastName?.trim() ?? "";
  const first = p.firstName?.trim() ?? "";
  if (last && first) {
    return `${last}, ${first}`;
  }
  if (last) {
    return last;
  }
  if (first) {
    return first;
  }
  return "—";
}

export function participantInitials(p: ParticipantRow): string {
  const f = p.firstName?.trim()?.[0] ?? "";
  const l = p.lastName?.trim()?.[0] ?? "";
  return (f + l).toUpperCase() || "?";
}

export function participantCardBackgroundSx(statusKey: string | undefined | null) {
  const key = canonicalEmergencyStatusKey(statusKey);
  if (key === "not-reported") {
    return {
      background:
        "linear-gradient(145deg, rgba(255, 241, 118, 0.55) 0%, rgba(255, 213, 79, 0.4) 45%, rgba(255, 193, 7, 0.35) 100%)",
    };
  }
  if (key === "safe") {
    return {
      background:
        "linear-gradient(145deg, rgba(165, 214, 167, 0.55) 0%, rgba(102, 187, 106, 0.4) 50%, rgba(56, 142, 60, 0.35) 100%)",
    };
  }
  /** Aligns with `emergencyStatusMapPinColor` / district `StatusCountChips` (#1565c0 + alpha) for this status. */
  if (key === "absent-offsite") {
    return {
      background:
        "linear-gradient(145deg, rgba(187, 222, 251, 0.65) 0%, rgba(100, 181, 246, 0.42) 50%, rgba(21, 101, 192, 0.3) 100%)",
    };
  }
  return {
    background:
      "linear-gradient(145deg, rgba(239, 154, 154, 0.5) 0%, rgba(229, 115, 115, 0.4) 50%, rgba(211, 47, 47, 0.35) 100%)",
  };
}

/** Keeps roster cards within grid cells so content cannot extend past the card; centers avatars to the visible area. */
export const participantRosterCardContainSx = {
  minWidth: 0,
  overflow: "hidden",
} as const;

export const participantRosterCardContentSx = {
  /** MUI CardContent adds extra bottom padding on last child; keep padding symmetric. */
  "&:last-child": {
    pb: 2,
  },

  boxSizing: "border-box",

  display: "flex",

  flex: 1,

  flexDirection: "column",

  minWidth: 0,

  overflow: "hidden",

  p: 2,

  width: "100%",
} as const;

export const participantRosterCardStackSx = {
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
} as const;

/** Text block: long lines wrap inside the card (alignment per row). */
export const participantRosterCardTextColumnSx = {
  alignSelf: "stretch",
  boxSizing: "border-box",
  minWidth: 0,
  overflowWrap: "break-word",
  width: "100%",
  wordBreak: "break-word",
} as const;

/** Role, status, last update (and similar detail lines). */
export const participantRosterCardDetailRowSx = {
  textAlign: "left",
} as const;

export const participantRosterCardCenteredRowSx = {
  textAlign: "center",
} as const;

export function rosterMatchesSearch(p: ParticipantRow, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) {
    return true;
  }
  const parts = [p.firstName, p.lastName, p.phoneNumber, ...(p.bubbleRoles ?? [])]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  const firstLast = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim().toLowerCase();
  const lastFirst =
    p.lastName?.trim() && p.firstName?.trim() ? `${p.lastName.trim()}, ${p.firstName.trim()}`.toLowerCase() : "";
  return parts.some((s) => s.includes(t)) || firstLast.includes(t) || (lastFirst.length > 0 && lastFirst.includes(t));
}

/** When `selectedIds` is empty, no status filter. Otherwise include participants in any selected bucket (OR). */
export function participantMatchesSummaryFilter(p: ParticipantRow, selectedIds: string[]): boolean {
  if (selectedIds.length === 0) {
    return true;
  }
  const key = canonicalEmergencyStatusKey(p.statusKey ?? "not-reported");
  if (KNOWN_STATUS_VALUES.has(key)) {
    return EVERYONE_SUMMARY_GROUPS.some((g) => selectedIds.includes(g.id) && g.statusValues.includes(key));
  }
  return selectedIds.includes(OTHER_LEGACY_SUMMARY_ID);
}
